import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AlertMessagesContext } from 'react-alert-messages';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { ShopContext } from 'Context/ShopContext';
import DataTable from 'components/utils/DataTable/';
import Info from 'components/utils/Alert/Info';
import styles from './styles.module.css';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import SyncIcon from '@mui/icons-material/Sync';
import Grayout from 'components/utils/GrayOut/Grayout';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import ShiftService from 'services/ShiftService';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { Tooltip, IconButton, Button, Grid, Typography } from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'const';
import { useTranslation } from 'react-i18next';

function Shifts() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const history = useHistory();
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const { startDate: initialStart, endDate: initialEnd } = dynamicDateRange('SHIFTS');
  const [startDate, setStartDate] = useState(initialStart);
  const [endDate, setEndDate] = useState(initialEnd);
  const [shifts, setShifts] = useState(null);
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [prevFilterData, setPrevFilterData] = useState({
    startDate: initialStart,
    endDate: initialEnd,
  });
  const [filteredShifts, setFilteredShifts] = useState(null);

  const getShifts = useCallback(async (start = startDate, end = endDate) => {
    setLoadingIndicator(true);
    try {
      const params = {
        start_date: DateTimeHelpers.getTimezoneOffsetDate(start),
        end_date: DateTimeHelpers.getTimezoneOffsetDate(end),
      };
      const resp = await ShiftService.getShifts(params);
      setShifts(resp);
      setFilteredShifts(resp);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  }, [startDate, endDate, postAlertMessage]);

  const loadShifts = useCallback((start, end) => {
    const actualStart = start instanceof Date ? start : startDate;
    const actualEnd = end instanceof Date ? end : endDate;

    getShifts(actualStart, actualEnd);
    setIsShowGenerateButton(false);
    setPrevFilterData({
      startDate: actualStart,
      endDate: actualEnd,
    });
  }, [getShifts, startDate, endDate]);

  const handleDetailsClick = (shift) => {
    history.push(ROUTES.SHIFT_SUMMARY.replace(':shiftId', shift.id));
  };

  const headerData = [
    {
      label: tCommon('ID'),
      id: 'id',
      type: 'text',
    },
    {
      label: tCommon('Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Terminal'),
      id: 'terminal',
      type: 'callback',
      viewRender: (obj) => {
        return obj.terminal.name;
      },
    },
    {
      label: tCommon('Opened at'),
      id: 'opened_at',
      type: 'callback',
      viewRender: (obj) => {
        return DateTimeHelpers.formatDateTime(obj.opened_at);
      },
    },
    {
      label: tCommon('Closed at'),
      id: 'closed_at',
      type: 'callback',
      viewRender: (obj) => {
        if (!obj.closed_at) return '-';
        return DateTimeHelpers.formatDateTime(obj.closed_at);
      },
    },
    {
      label: tCommon('Actions'),
      id: 'actions',
      type: 'callback',
      viewRender: (item) => {
        return (
          <Grid className={styles.btnGroup}>
            <Tooltip title={tCommon('View Details')}>
              <IconButton onClick={() => handleDetailsClick(item)}>
                <RemoveRedEyeIcon className={styles.iconButton} />
              </IconButton>
            </Tooltip>
          </Grid>
        );
      },
    },
  ];

  const generateShiftReport = useCallback(() => {
    const { startDate: initialStart, endDate: initialEnd } = dynamicDateRange('SHIFTS');

    setStartDate(initialStart);
    setEndDate(initialEnd);

    loadShifts(initialStart, initialEnd);
  }, [loadShifts]);

  const isAnyChangeOnReportsFilters = useCallback(() => {
    if (!startDate || !prevFilterData.startDate || !endDate || !prevFilterData.endDate) {
      return false;
    }
    return (
      startDate.getTime() !== prevFilterData.startDate.getTime() ||
      endDate.getTime() !== prevFilterData.endDate.getTime()
    );
  }, [startDate, endDate, prevFilterData.startDate, prevFilterData.endDate]);

  useEffect(() => {
    generateShiftReport();
    //eslint-disable-next-line
  }, [shop]);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tCommon('Shifts')}
          <span className={styles.menuTitle}>{tCommon('Management')}</span>
        </h2>
      </div>
      <div className={styles.changeable}>
        <Grid container spacing={1} alignItems="flex-end">
          {/* Title */}
          <Grid item xs={12}>
            <Typography variant="h6">
              {tReport('Shift from {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(startDate),
                to: DateTimeHelpers.convertDateToDMY(endDate),
              })}
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <DateTimeRange
              setFilterFromDate={setStartDate}
              setFilterToDate={setEndDate}
              storageKey="SHIFTS"
              showTimeSelector={false}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography variant="body2" style={{ marginBottom: 4, fontWeight: 500 }}>
                {tReport('Load Shifts')}
              </Typography>

              <Button
                variant="contained"
                style={{
                  backgroundColor: '#00a65a',
                  color: '#fff',
                  height: 40,
                  minWidth: 160,
                }}
                onClick={loadShifts}
                disabled={isShowGenerateButton === false}
                startIcon={<SyncIcon />}
              >
                {tCommon('Generate')}
              </Button>
            </div>
          </Grid>
        </Grid>
      </div>
      {(filteredShifts || loadingIndicator) &&
        ((filteredShifts && filteredShifts.length) || loadingIndicator ? (
          <div className={styles.tableContainer}>
            <Grayout open={isShowGenerateButton} />
            <DataTable columns={headerData} rows={filteredShifts || shifts || []} isLoading={loadingIndicator} />
          </div>
        ) : (
          !loadingIndicator && (
            <Info
              title={tReport('No shifts with the current filter')}
              content={tReport(
                'You have no shifts to list with current filter configuration. Please clear the filters or create a shifts'
              )}
            />
          )
        ))}
    </div>
  );
}

export default withConsoleBase(Shifts);
